import AntIcon from "@/components/icons/AntIcon";
import { getDatePickerDefaultRanges, getDatePickerFormat } from "@/utils/formUtils";
import { useWindowSize } from "@/utils/hooksUtils";
import { cssVariables, dateToIsoDateString, isDefined } from "@/utils/utils";
import { Button, Col, DatePicker, Flex, Row } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import eicLogo from "../../../../../assets/images/institutions/eic.png";
import iadLogo from "../../../../../assets/images/institutions/iad.svg";
import ibisLogo from "../../../../../assets/images/institutions/ibis.svg";
import { useGetDashboardPortfolio } from "../../../../../common/api/queries";
import { DATA_UNIT } from "../../../enums";
import { DashboardPortfolioContracts } from "../../../types";
import { DASHBOARD_MIDLLE_SCREEN_UPDATE_WIDTH } from "../../../utils";
import { PortfolioCard, PortfolioCardData, PortfolioCardLabels } from "./components/PortfolioCard";

type PortfolioContractsType = "insurance" | "investment" | "ibis";

const INVESTMENT_SWITCH_WIDTH = 280;

const BorderedLogo = ({ children }: { children: ReactNode }) => (
  <Flex
    style={{
      display: "inline-flex",
      width: 40,
      height: 40,
      borderRadius: 12,
      border: `1px solid ${cssVariables.colorShadow}`,
    }}
    align="center"
    justify="center"
  >
    {children}
  </Flex>
);

const getContractsData = (contracts?: DashboardPortfolioContracts): PortfolioCardData => ({
  [DATA_UNIT.AMOUNT_SUM]:
    isDefined(contracts?.groupAmount) && isDefined(contracts.individualAmount)
      ? { leftAmount: contracts.groupAmount, rightAmount: contracts.individualAmount }
      : undefined,
  [DATA_UNIT.CONTRACT_COUNT]: { leftAmount: contracts?.groupCount, rightAmount: contracts?.individualCount },
});

export const DashboardPortfolioWidget = () => {
  const { t } = useTranslation();

  const windowSize = useWindowSize();

  const [dates, setDates] = useState<[Dayjs, Dayjs]>(() => [dayjs().startOf("month"), dayjs()]);

  const { data, isFetching } = useGetDashboardPortfolio({
    minDate: dateToIsoDateString(dates[0]),
    maxDate: dateToIsoDateString(dates[1]),
  });

  const getContractsLabels = (type: PortfolioContractsType): PortfolioCardLabels => {
    const hintsPath = `dashboard.portfolio.helpers.contractLabelHints.${type}`;

    return {
      [DATA_UNIT.AMOUNT_SUM]: {
        option:
          type === "investment"
            ? t("dashboard.portfolio.helpers.totalAssets")
            : t("dashboard.enums.dataUnit.AMOUNT_SUM"),
        left: { title: t("dashboard.portfolio.helpers.group"), hint: t(`${hintsPath}.group.${DATA_UNIT.AMOUNT_SUM}`) },
        right: {
          title: t("dashboard.portfolio.helpers.individual"),
          hint: t(`${hintsPath}.individual.${DATA_UNIT.AMOUNT_SUM}`),
        },
      },
      [DATA_UNIT.CONTRACT_COUNT]: {
        option: t("dashboard.enums.dataUnit.CONTRACT_COUNT"),
        left: {
          title: t("dashboard.portfolio.helpers.group"),
          hint: t(`${hintsPath}.group.${DATA_UNIT.CONTRACT_COUNT}`),
        },
        right: {
          title: t("dashboard.portfolio.helpers.individual"),
          hint: t(`${hintsPath}.individual.${DATA_UNIT.CONTRACT_COUNT}`),
        },
      },
    };
  };

  return (
    <Flex vertical style={{ height: "100%" }}>
      <Flex justify="space-between" style={{ height: 46 }}>
        <h2 style={{ fontWeight: 600, fontSize: 28, lineHeight: "36px" }}>{t("dashboard.portfolio.title")}</h2>

        <div style={{ marginBottom: 14 }}>
          <DatePicker.RangePicker
            style={{ borderRadius: 18 }}
            format={getDatePickerFormat()}
            presets={getDatePickerDefaultRanges({ maxDate: dayjs() })}
            maxDate={dayjs()}
            placeholder={[t("common.from"), t("common.to")]}
            value={dates}
            onChange={(value) => setDates(value as [Dayjs, Dayjs])}
            allowClear={false}
          />
        </div>
      </Flex>

      <Flex vertical flex={1} style={{ overflow: "hidden", position: "relative" }}>
        <Row
          gutter={windowSize.innerWidth >= DASHBOARD_MIDLLE_SCREEN_UPDATE_WIDTH ? 24 : 12}
          style={{
            height: "50%",
            paddingBottom: windowSize.innerWidth >= DASHBOARD_MIDLLE_SCREEN_UPDATE_WIDTH ? 12 : 6,
          }}
        >
          <Col span={12} style={{ height: "100%" }}>
            <PortfolioCard
              isLoading={isFetching}
              logo={
                <Button
                  variant="filled"
                  size="large"
                  color="blue"
                  style={{ fontSize: 24 }}
                  icon={<AntIcon type="safety" />}
                  disabled={!data?.lifeInsuranceContracts}
                />
              }
              title={t("dashboard.portfolio.titles.lifeInsuranceContracts")}
              data={getContractsData(data?.lifeInsuranceContracts)}
              labels={getContractsLabels("insurance")}
              disabled={!data?.lifeInsuranceContracts}
            />
          </Col>

          <Col span={12} style={{ height: "100%" }}>
            <PortfolioCard
              isLoading={isFetching}
              logo={<img src={ibisLogo} alt="icon" style={{ width: 36 }} />}
              title={t("dashboard.portfolio.titles.ibisContracts")}
              data={getContractsData(data?.ibisContracts)}
              labels={getContractsLabels("ibis")}
              disabled={!data?.ibisContracts}
            />
          </Col>
        </Row>

        <Row
          gutter={windowSize.innerWidth >= DASHBOARD_MIDLLE_SCREEN_UPDATE_WIDTH ? 24 : 12}
          style={{ height: "50%", paddingTop: windowSize.innerWidth >= DASHBOARD_MIDLLE_SCREEN_UPDATE_WIDTH ? 12 : 6 }}
        >
          <Col span={12} style={{ height: "100%" }}>
            <PortfolioCard
              isLoading={isFetching}
              logo={
                <BorderedLogo>
                  <img src={iadLogo} alt="icon" style={{ width: 30 }} />
                </BorderedLogo>
              }
              title={t("dashboard.portfolio.titles.iadContracts")}
              data={getContractsData(data?.iadContracts)}
              labels={getContractsLabels("investment")}
              switchWidth={INVESTMENT_SWITCH_WIDTH}
              disabled={!data?.iadContracts}
            />
          </Col>

          <Col span={12} style={{ height: "100%" }}>
            <PortfolioCard
              isLoading={isFetching}
              logo={
                <BorderedLogo>
                  <img
                    src={eicLogo}
                    alt="icon"
                    style={{ width: 34, height: 30, objectFit: "cover", objectPosition: "left center" }}
                  />
                </BorderedLogo>
              }
              title={t("dashboard.portfolio.titles.eicContracts")}
              data={getContractsData(data?.eicContracts)}
              labels={getContractsLabels("investment")}
              switchWidth={INVESTMENT_SWITCH_WIDTH}
              disabled={!data?.eicContracts}
            />
          </Col>
        </Row>
      </Flex>
    </Flex>
  );
};
